import { useEffect, useState } from 'react';
import { Bell, X, AlertTriangle, Copy, Mail, CheckCircle2, Tag } from 'lucide-react';
import { Drawer, DrawerContent, DrawerTitle } from '../ui/drawer';
import { formatAmount, formatShortDay } from '../../lib/constants';
import { useAmountsVisibility } from '../../context/AmountsVisibilityContext';

// Inicio's notification bell (redesign.md, Fase 4). There's no stored inbox:
// the sheet lists what's worth a look right now, computed live by
// GET /dashboard — budgets over/near their limit, possible duplicates to
// review, and movements Gmail detected in the last 24h. The badge counts the
// items not seen yet on this device (ids remembered in localStorage).
const SEEN_KEY = 'mp_alerts_seen';

function readSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); } catch { return new Set(); }
}

export default function AlertsBell({ dashboard, onOpenTransactions, onOpenCategories }) {
  const { hidden } = useAmountsVisibility();
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(readSeen);

  const budgetAlerts = (dashboard?.alerts || []).filter(a => a.level === 'exceeded' || a.level === 'critical');
  const duplicates = dashboard?.possible_duplicates || [];
  const detected = dashboard?.email_detected_recent || [];
  const misfiled = dashboard?.misfiled || [];
  const ids = [
    ...misfiled.map(t => `misfiled:${t.id}`),
    ...budgetAlerts.map(a => `budget:${a.category_id}:${a.level}`),
    ...duplicates.map(t => `dup:${t.id}`),
    ...detected.map(t => `email:${t.id}`),
  ];
  const unseen = ids.filter(id => !seen.has(id)).length;
  const total = ids.length;

  // Opening the sheet marks everything in it as seen (keeps only current ids
  // so the stored list doesn't grow forever).
  useEffect(() => {
    if (!open || ids.length === 0) return;
    const next = new Set(ids);
    setSeen(next);
    try { localStorage.setItem(SEEN_KEY, JSON.stringify([...next])); } catch { /* private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const go = (fn) => { setOpen(false); fn && fn(); };
  const row = 'w-full min-h-[56px] flex items-start gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-white/5 transition-colors';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={unseen ? `Notificaciones, ${unseen} nuevas` : 'Notificaciones'}
        data-testid="alerts-bell"
        className="relative w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-slate-300 hover:bg-white/5 transition-colors"
      >
        <Bell className="w-6 h-6" strokeWidth={1.75} />
        {unseen > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-on-brand text-[10px] font-bold flex items-center justify-center" data-testid="alerts-badge">
            {unseen > 9 ? '9+' : unseen}
          </span>
        )}
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="bg-slate-900 border-white/10 text-slate-200 pb-safe max-h-[85vh]" data-testid="alerts-sheet">
          <DrawerTitle className="sr-only">Notificaciones</DrawerTitle>
          <div className="flex items-center justify-between px-5 pb-2">
            <span className="text-base font-semibold text-white">Notificaciones</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
              className="w-11 h-11 -mr-2 rounded-xl flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-2 pb-6 overflow-y-auto space-y-4">
            {total === 0 && (
              <div className="flex flex-col items-center text-center py-8 px-6" data-testid="alerts-empty">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" strokeWidth={1.75} />
                <p className="text-sm font-medium text-white">Todo en orden</p>
                <p className="text-xs text-slate-500 mt-1">Aquí verás presupuestos al límite, posibles duplicados, movimientos con la categoría equivocada y gastos detectados por Gmail.</p>
              </div>
            )}

            {budgetAlerts.length > 0 && (
              <div>
                <p className="px-3 mb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Presupuestos</p>
                {budgetAlerts.map(a => {
                  const over = a.spent - a.budget;
                  return (
                    <button key={a.category_id} type="button" className={row} onClick={() => go(onOpenCategories)}>
                      <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${a.level === 'exceeded' ? 'text-rose-400' : 'text-amber-400'}`} />
                      <span className="text-sm text-slate-200">
                        {a.level === 'exceeded'
                          ? <>Te pasaste {formatAmount(over, hidden)} en <strong className="font-semibold">{a.category_name}</strong></>
                          : <>Llevas el {Math.round(a.percentage)}% de <strong className="font-semibold">{a.category_name}</strong> · quedan {formatAmount(a.budget - a.spent, hidden)}</>}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {duplicates.length > 0 && (
              <div>
                <p className="px-3 mb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Posibles duplicados</p>
                {duplicates.map(t => (
                  <button key={t.id} type="button" className={row} onClick={() => go(onOpenTransactions)}>
                    <Copy className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
                    <span className="min-w-0 text-sm text-slate-200">
                      <span className="block truncate">{t.description || t.category_name} · {formatAmount(t.amount, hidden)}</span>
                      <span className="block text-xs text-slate-500">{formatShortDay(t.date)} · revísalo en Movimientos</span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {misfiled.length > 0 && (
              <div>
                <p className="px-3 mb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Categoría por revisar</p>
                {misfiled.map(t => (
                  <button key={t.id} type="button" className={row} onClick={() => go(onOpenTransactions)}>
                    <Tag className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
                    <span className="min-w-0 text-sm text-slate-200">
                      <span className="block truncate">{t.description || t.category_name} · {formatAmount(t.amount, hidden)}</span>
                      <span className="block text-xs text-slate-500">
                        {t.type === 'expense' ? 'Gasto' : 'Ingreso'} en {t.category_name}, una categoría de {t.type === 'expense' ? 'ingresos' : 'gastos'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {detected.length > 0 && (
              <div>
                <p className="px-3 mb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">Detectados por Gmail</p>
                {detected.map(t => (
                  <button key={t.id} type="button" className={row} onClick={() => go(onOpenTransactions)}>
                    <Mail className="w-4 h-4 mt-0.5 shrink-0 text-violet-400" />
                    <span className="min-w-0 text-sm text-slate-200">
                      <span className="block truncate">{t.description || t.category_name}</span>
                      <span className="block text-xs text-slate-500">
                        {t.type === 'income' ? '+' : '−'}{formatAmount(t.amount, hidden)} · {t.category_name} · {formatShortDay(t.date)}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
